import { Theme } from '@mui/material'
import { EnqueueSnackbar } from 'notistack'

import Api from '../../../../utils/api'
import { isGateway } from '../../../../utils/helpers'

import { DeviceTableGroupRow, DeviceTableRow, GroupByKey, GroupByProperties } from './types'

const groupToPropertyMap: { [key in GroupByKey]: GroupByProperties } = {
  alertSeverity: 'alertStatus',
  deviceHealth: 'deviceHealthStatus',
  status: 'deviceStatus',
  gateway: 'gatewayId',
  deviceType: 'nodeTypeVanity',
  solution: 'deviceUsecase',
  location: 'deviceLocation',
  none: undefined,
}

export const alertSeveritySortOrder: { [x: string]: number } = {
  urgent: 1,
  warning: 2,
  info: 3,
  none: 4,
  inactive: 5,
}

const deviceHealthSortOrder: { [x: string]: number } = {
  'Low Battery & Signal': 1,
  'Low Battery': 2,
  'Low Signal': 3,
  'No Health Issues': 4,
  Inactive: 5,
}

const deviceStatusSortOrder: { [x: string]: number } = {
  Online: 1,
  Offline: 2,
  Paused: 3,
  Extra: 4,
  'Not Activated': 5,
  Deactivated: 6,
}

const sortDevices = (results: DeviceTableRow[], deviceGroupBy: GroupByKey, property?: GroupByProperties) => {
  let sortedDevices = [...results]

  // first sort all devices by alert status regardless of deviceGroupBy
  sortedDevices.sort((a, b) => {
    if (alertSeveritySortOrder[a.alertStatus] < alertSeveritySortOrder[b.alertStatus]) return -1
    return 1
  })

  // if sorting by gateways, sort gateways at the top
  if (deviceGroupBy === 'gateway')
    sortedDevices = sortedDevices.sort((a, b) => {
      if (isGateway(a.nodeType)) return -1
      if (isGateway(b.nodeType)) return 1
      return 0
    })

  // sort devices by property
  if (property && deviceGroupBy !== 'alertSeverity')
    sortedDevices.sort((a, b) => {
      if (deviceGroupBy === 'status') {
        if (deviceStatusSortOrder[a[property]] < deviceStatusSortOrder[b[property]]) return -1
        return 1
      }
      if (deviceGroupBy === 'deviceHealth') {
        if (deviceHealthSortOrder[a[property]] < deviceHealthSortOrder[b[property]]) return -1
        return 1
      }

      // if sorting by gateways, empty gatewayId should be at the end
      if (deviceGroupBy === 'gateway') {
        if (!a[property] && !b[property]) return 0
        if (!a[property]) return 1
        if (!b[property]) return -1
      }

      if (a[property] < b[property]) return -1
      return 1
    })

  // populate isFirstOfType if this is the first device with this property. populate isLastOfType if this is the last device with this property
  sortedDevices = sortedDevices.map((d, i) => ({
    ...d,
    isFirstOfType: i === 0 || (property && d[property] !== sortedDevices[i - 1][property]) || false,
    isLastOfType: i === sortedDevices.length - 1 || (property && d[property] !== sortedDevices[i + 1][property]) || false,
  }))
  return sortedDevices
}

const groupDevicesBy = (results: (DeviceTableRow | DeviceTableGroupRow)[], deviceGroupBy: GroupByKey, property: GroupByProperties, theme: Theme) => {
  const blueColorSchemes = [
    theme.palette.blue.variant1,
    theme.palette.blue.variant2,
    theme.palette.blue.variant3,
    theme.palette.blue.variant4,
    theme.palette.blue.variant5,
  ] as string[]
  let groupId = -1
  const createdGroups = new Set<string>()
  let resultsWithGroups = [...results]
  resultsWithGroups = sortDevices(resultsWithGroups as DeviceTableRow[], deviceGroupBy, property)

  if (property) {
    // add grouping rows before each group of devices of the same group
    resultsWithGroups = resultsWithGroups.reduce(
      (acc, item) => {
        const row = item as DeviceTableRow
        const propertyValue = String(row[property])
        const originalResults = results as DeviceTableRow[]

        if (!createdGroups.has(propertyValue)) {
          groupId += 1
          const id = groupId
          let groupColor = theme.palette.text.secondary
          if (deviceGroupBy === 'alertSeverity' || deviceGroupBy === 'status' || deviceGroupBy === 'deviceHealth') {
            if (propertyValue === 'info' || propertyValue === 'Extra' || propertyValue === 'Not Activated') groupColor = theme.palette.info.main
            if (propertyValue === 'warning' || propertyValue === 'Paused' || propertyValue === 'Low Signal' || propertyValue === 'Low Battery')
              groupColor = theme.palette.warning.main
            if (propertyValue === 'urgent' || propertyValue === 'Offline' || propertyValue === 'Low Battery & Signal') groupColor = theme.palette.error.main
            if (propertyValue === 'none' || propertyValue === 'Online' || propertyValue === 'No Health Issues') groupColor = theme.palette.success.main
            if (propertyValue === 'inactive' || propertyValue === 'Deactivated' || propertyValue === 'Inactive') groupColor = theme.palette.text.secondary
          } else {
            // select color from blueColorSchemes, cycling through the list
            groupColor = blueColorSchemes[id % blueColorSchemes.length]
          }

          acc.push({
            id: `group-${id}`,
            group: propertyValue,
            groupColor,
            isGroupRow: true,
            count: originalResults.filter((i) => String(i[property]) === propertyValue).length,
          })
          createdGroups.add(propertyValue)
        }
        acc.push(row)
        return acc
      },
      [] as (DeviceTableRow | DeviceTableGroupRow)[]
    )

    // populate group information for device rows
    resultsWithGroups = resultsWithGroups.map((item) => {
      if ((item as DeviceTableGroupRow).isGroupRow) return item
      const groupRow = resultsWithGroups.find((g) => g.group === String((item as DeviceTableRow)[property])) as DeviceTableGroupRow
      return {
        ...item,
        group: groupRow?.group || '',
        groupColor: groupRow?.groupColor || theme.palette.text.secondary,
      }
    })
  } else {
    resultsWithGroups = resultsWithGroups.map((item) => {
      let groupColor = theme.palette.text.secondary
      const row = item as DeviceTableRow
      if (row.alertStatus === 'info') groupColor = theme.palette.info.main
      if (row.alertStatus === 'warning') groupColor = theme.palette.warning.main
      if (row.alertStatus === 'urgent') groupColor = theme.palette.error.main
      if (row.alertStatus === 'none') groupColor = theme.palette.success.main
      if (row.alertStatus === 'inactive') groupColor = theme.palette.text.secondary

      return {
        ...item,
        group: '',
        groupColor,
      }
    })
  }

  return resultsWithGroups
}

export const groupDevices = (results: DeviceTableRow[], deviceGroupBy: GroupByKey, theme: Theme) =>
  groupDevicesBy(results as (DeviceTableRow | DeviceTableGroupRow)[], deviceGroupBy, groupToPropertyMap[deviceGroupBy], theme)

export const getDevices = async (folderId: number, host: string) => {
  const devices: IDeviceAPIObject[] = []

  const getDevicesApi = async (
    offset = 0
  ): Promise<{
    status: number
    statusText: string
    ok: boolean
    devices: IDeviceAPIObject[]
  }> => {
    const response = await Api.get(`${host}/api/v2/central/folders/${folderId}/devices?offset=${offset}&limit=100`)
    if (response.ok) {
      devices.push(...((response?.body?.rows as IDeviceAPIObject[]) ?? []))
      if (response?.body?.hasMore) {
        // Call the function again (recursively continue to fetch devices until hasMore is false)
        return getDevicesApi(offset + 100)
      }
    }
    // Return the data
    return { status: response.status, statusText: response?.statusText, ok: response.ok, devices }
  }

  return getDevicesApi()
}

export const onSavePhoto = async ({
  getFolderDevices,
  setDevicePhotoRefreshCounter,
  setViewDevicePhoto,
  enqueueSnackbar,
}: {
  getFolderDevices: () => Promise<void>
  setDevicePhotoRefreshCounter: React.Dispatch<React.SetStateAction<number>>
  setViewDevicePhoto: React.Dispatch<React.SetStateAction<DeviceTableRow | undefined>>
  enqueueSnackbar: EnqueueSnackbar
}) => {
  await getFolderDevices() // Refresh devices after photo update

  // Increment counter to trigger photo refresh on device edit modal
  setDevicePhotoRefreshCounter((prev) => prev + 1)

  setViewDevicePhoto(undefined)
  enqueueSnackbar('Device photo updated successfully.', { variant: 'success' })
}
